import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OrganizationList } from "../../Redux/Actions/OrganizationAction";
import { Modal, TextField, CircularProgress, Typography } from "@mui/material";
import { ExtractQuestionPdfAction, BulkCreateMCQAction } from "../../Redux/Actions/QuestionVerifyAction";
import { EXTRACT_QUESTION_PDF_RESET, BULK_CREATE_MCQ_RESET } from "../../Constants/QuestionVerifyConstants";

interface BulkUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<any>();
  let GetOrganizationData = useSelector((state: any) => state?.OrganizationListData);
  let ExtractQuestionPdfData = useSelector((state: any) => state?.ExtractQuestionPdfState);
  let BulkCreateMCQData = useSelector((state: any) => state?.BulkCreateMCQState);
  const [orgList, setOrgList] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [orgSearch, setOrgSearch] = useState<string>("");
  const [orgPage, setOrgPage] = useState<number>(1);
  const [orgTotalCount, setOrgTotalCount] = useState<number>(0);
  const [isFetchingOrgs, setIsFetchingOrgs] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [extractedQuestions, setExtractedQuestions] = useState<any[]>([]);

  const handleBulkCreate = () => {
    if (!selectedOrg || extractedQuestions?.length === 0) return;
    const payload = {
      org_id: parseInt(selectedOrg),
      questions: extractedQuestions
    };
    dispatch(BulkCreateMCQAction(payload));
  };

  useEffect(() => {
    if (!BulkCreateMCQData?.loading && BulkCreateMCQData?.status && BulkCreateMCQData?.data) {
      onClose();
    }
  }, [BulkCreateMCQData, onClose]);

  useEffect(() => {
    if (!open) return;
    const searchParam = orgSearch?.trim() ? `&name=${orgSearch?.trim()}` : "";
    setIsFetchingOrgs(true);
    dispatch(OrganizationList(`?page=${orgPage}&page_size=10${searchParam}`));
  }, [open, orgPage, orgSearch, dispatch]);

  useEffect(() => {
    if (!GetOrganizationData?.loading) {
      setIsFetchingOrgs(false);
    }
    if (!GetOrganizationData?.loading && GetOrganizationData?.status && GetOrganizationData?.data) {
      const nextRows = GetOrganizationData?.data?.row || [];
      const totalCount = GetOrganizationData?.data?.total_count || 0;
      setOrgTotalCount(totalCount);
      setOrgList((prev) => (orgPage === 1 ? nextRows : [...prev, ...nextRows]));
    }
  }, [GetOrganizationData, orgPage]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setSelectedOrg("");
      setOrgSearch("");
      setOrgPage(1);
      setOrgTotalCount(0);
      setOrgList([]);
      setIsFetchingOrgs(false);
      setExtractedQuestions([]);
      dispatch({ type: EXTRACT_QUESTION_PDF_RESET });
      dispatch({ type: BULK_CREATE_MCQ_RESET });
    }
  }, [open, dispatch]);

  const handleOrgListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isFetchingOrgs || GetOrganizationData?.loading) return;
    if (orgList?.length >= orgTotalCount && orgTotalCount !== 0) return;

    const target = e.currentTarget;
    const scrollBuffer = 40;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - scrollBuffer;

    if (reachedBottom) {
      setOrgPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (!ExtractQuestionPdfData?.loading && ExtractQuestionPdfData?.status && ExtractQuestionPdfData?.data) {
      const questionsList = ExtractQuestionPdfData?.data?.questions || ExtractQuestionPdfData?.data?.mcq_questions;
      if (questionsList) {
        setExtractedQuestions(questionsList);
      }
    }
  }, [ExtractQuestionPdfData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files && e?.target?.files?.length > 0) {
      const selected = e?.target?.files[0];
      if (selected?.type !== "application/pdf") {
        setFileError("Only PDF files are allowed.");
        setFile(null);
        return;
      }
      if (selected?.size > 10 * 1024 * 1024) {
        setFileError("File size must be less than 10 MB.");
        setFile(null);
        return;
      }
      setFileError("");
      setFile(selected);
    }
  };

  const handleExtract = () => {
    if (!file || !selectedOrg) return;
    const formData = new FormData();
    formData?.append("file", file);
    formData?.append("org_id", selectedOrg);
    dispatch(ExtractQuestionPdfAction(formData));
  };

  const formatStringList = (value: unknown) => {
    if (!Array.isArray(value) || value.length === 0) return "-";
    return value.map((v) => String(v)).filter(Boolean).join(", ") || "-";
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      className="admin-panel-modal-container"
    >
      <div className={`admin-panel-modal ${extractedQuestions?.length > 0 ? "bulk-upload-modal-expanded" : "bulk-upload-modal-default"}`}>
        <div className="admin-panel-modal-heading-container">
          <h4 className="admin-panel-modal-heading">Bulk Upload Questions</h4>
          <button
            type="button"
            onClick={onClose}
            className="admin-panel-modal-close-btn"
          >
            <span className="mdi mdi-window-close"></span>
          </button>
        </div>
        <div className="admin-panel-modal-separator"></div>
        <div className={`admin-panel-main-container ${extractedQuestions?.length > 0 ? "bulk-upload-scroll-container" : ""}`}>
          {extractedQuestions?.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-bordered table-striped mt-3 bulk-upload-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Options</th>
                    <th>Correct Answer</th>
                    <th>Difficulty</th>
                    <th>Beans</th>
                    <th>Tags</th>
                    <th>Topics</th>
                    <th>Company tags</th>
                    <th>Images</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedQuestions?.map((q: any, i: number) => {
                    const correctAnswer = q?.options?.find((o: any) => o?.is_correct)?.text;
                    return (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{q?.question_type || "-"}</td>
                      <td>{q?.title || "-"}</td>
                      <td>{q?.description || "-"}</td>
                      <td>
                        {q?.options && q?.options?.length > 0 ? (
                          <ul className="bulk-upload-options-list">
                            {q?.options?.map((opt: any, idx: number) => (
                              <li key={idx} className={`bulk-upload-option-item ${opt?.is_correct ? "bulk-upload-option-correct" : ""}`}>
                                {opt?.text}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{correctAnswer || "-"}</td>
                      <td className="bulk-upload-difficulty">{q?.difficulty || "-"}</td>
                      <td>{q?.coins || "-"}</td>
                      <td className="bulk-upload-tags-cell">{formatStringList(q?.tags)}</td>
                      <td className="bulk-upload-tags-cell">{formatStringList(q?.topics)}</td>
                      <td className="bulk-upload-tags-cell">{formatStringList(q?.company_tags)}</td>
                      <td>
                        {q?.image_url ? (
                          <div className="bulk-upload-image-wrapper">
                            <img src={q?.image_url} alt="question" className="bulk-upload-image" />
                          </div>
                        ) : "-"}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
              <div className="d-flex justify-content-end align-items-center w-100 gap-2 mt-4">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={onClose}
                  disabled={BulkCreateMCQData?.loading}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="primary-btn admin-panel-modal-submit-btn bulk-upload-submit-btn"
                  onClick={handleBulkCreate}
                  disabled={BulkCreateMCQData?.loading}
                >
                  {BulkCreateMCQData?.loading ? <CircularProgress size={24} color="inherit" /> : "Bulk Create"}
                </button>
              </div>
            </div>
          ) : (
            <div className="bulk-upload-form-wrapper">
              <div className="bulk-upload-org-select">
            <TextField
              fullWidth
              size="small"
              label="Search Organization"
              value={orgSearch}
              onChange={(e) => {
                setOrgSearch(e?.target?.value);
                setOrgPage(1);
                setOrgList([]);
              }}
              sx={{ mb: 1 }}
            />
            <div className="bulk-upload-org-list" onScroll={handleOrgListScroll}>
              {orgList?.length > 0 ? (
                orgList?.map((org: any) => (
                  <div
                    key={org?.id}
                    className={`bulk-upload-org-item ${selectedOrg === String(org?.id) ? "bulk-upload-org-item-selected" : ""}`}
                    onClick={() => setSelectedOrg(String(org?.id))}
                  >
                    {org?.org_name}
                  </div>
                ))
              ) : !(isFetchingOrgs || GetOrganizationData?.loading) ? (
                <div className="bulk-upload-org-empty">No organizations found</div>
              ) : null}
              {(isFetchingOrgs || GetOrganizationData?.loading) && (
                <div className="bulk-upload-org-empty">Loading organizations...</div>
              )}
            </div>
          </div>

          <div className="bulk-upload-file-section">
            <div className="bulk-upload-file-header">
              <div className="admin-panel-modal-label">
                Upload PDF File
              </div>
              <a
                href="/file/bulk_question_upload.pdf"
                download="bulk_question_upload.pdf"
                className="bulk-upload-sample-link"
              >
                <span className="mdi mdi-download bulk-upload-sample-link-icon"></span>
                Download Sample PDF
              </a>
            </div>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="form-control"
            />
            <Typography variant="caption" className={`bulk-upload-file-hint ${fileError ? "bulk-upload-file-hint-error" : ""}`}>
              {fileError || "Max 10 MB PDF file allowed "}
            </Typography>
            {file && (
              <Typography variant="caption" className="bulk-upload-file-selected">
                Selected: {file?.name}
              </Typography>
            )}
          </div>

          <div className="d-flex justify-content-end align-items-center w-100 gap-2">
            <button
              type="button"
              className="secondary-btn"
              onClick={onClose}
              disabled={ExtractQuestionPdfData?.loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary-btn admin-panel-modal-submit-btn bulk-upload-submit-btn"
              disabled={!selectedOrg || !file || ExtractQuestionPdfData?.loading}
              onClick={handleExtract}
            >
              {ExtractQuestionPdfData?.loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Extract Question"
              )}
            </button>
          </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BulkUploadModal;
